import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Modal, Space, Typography, message } from "antd";

import FileDetailsPanel from "./components/FileDetailsPanel";
import JsonEditorModal from "./components/JsonEditorModal";
import type { PanchangamFileOpsService } from "./services/panchangam-file-ops.service";
import { panchangamFileOpsService } from "./services/service-bootstrap";
import type { JsonValue, PanchangamFileContext } from "./types";
import { getPanchangamAccessForEmail } from "./utils/access";

export interface ConfigEditorDetailScreenProps {
  userEmail: string;
  folderId: string;
  fileId: string;
  fileOpsService?: PanchangamFileOpsService;
  onBack?: () => void;
}

export const ConfigEditorDetailScreen: React.FC<ConfigEditorDetailScreenProps> = ({ userEmail, folderId, fileId, fileOpsService = panchangamFileOpsService, onBack }) => {
  const access = useMemo(() => getPanchangamAccessForEmail(userEmail), [userEmail]);
  const canAccess = access.isAllowed;
  const canAdmin = access.isAdmin;

  const [fileContext, setFileContext] = useState<PanchangamFileContext | null>(null);
  const [latestContent, setLatestContent] = useState<JsonValue>({});
  const [deployedContent, setDeployedContent] = useState<JsonValue>({});
  const [showSafeEditor, setShowSafeEditor] = useState(false);
  const [safeEditorPrefillMode, setSafeEditorPrefillMode] = useState<"template" | "latest" | "previous">("template");
  const [previousVersionContent, setPreviousVersionContent] = useState<JsonValue>({});
  const [showAdvancedEditor, setShowAdvancedEditor] = useState(false);
  const [savingAction, setSavingAction] = useState(false);

  const activeFileId = useMemo(() => decodeURIComponent(fileId ?? ""), [fileId]);

  const extractVersionFromUrl = (url: string): number | null => {
    const match = url.match(/_v(\d+)\.json(?:\?|$)/i);
    if (!match) {
      return null;
    }

    const parsed = Number(match[1]);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const fetchFileContext = useCallback(async () => {
    if (!canAccess) {
      return;
    }

    if (!folderId || !activeFileId) {
      return;
    }

    try {
      const nextContext = await fileOpsService.loadFileContext({
        folderId,
        fileId: activeFileId,
      });

      setFileContext(nextContext);

      const latest = nextContext.latestVersionUrl
        ? await fileOpsService.readJsonAtUrl(nextContext.latestVersionUrl)
        : await fileOpsService.readJsonAtUrl(nextContext.file.static_url);

      setLatestContent(latest);

      if (nextContext.deployedVersion == null) {
        setDeployedContent({});
      } else {
        const deployedVersionUrl = nextContext.file.meta_data.previous_version.find(
          (url) => extractVersionFromUrl(url) === nextContext.deployedVersion,
        );

        if (deployedVersionUrl) {
          const deployed = await fileOpsService.readJsonAtUrl(deployedVersionUrl);
          setDeployedContent(deployed);
        } else {
          const deployed = await fileOpsService.readJsonAtUrl(nextContext.file.static_url);
          setDeployedContent(deployed);
        }
      }
    } catch (_error) {
      setFileContext(null);
      message.error("Failed to load Panchangam file details");
    }
  }, [canAccess, fileOpsService, folderId, activeFileId]);

  useEffect(() => {
    fetchFileContext();
  }, [fetchFileContext]);

  const getVersionTemplateContent = (): JsonValue => {
    if (fileContext?.deployedVersion != null) {
      return deployedContent;
    }

    return latestContent;
  };

  const persistEditorSave = async (payload: { value: JsonValue; resetSchema: boolean }) => {
    if (!fileContext || !folderId) {
      throw new Error("No active file selected");
    }

    const blob = new Blob([JSON.stringify(payload.value, null, 2)], {
      type: "application/json",
    });
    const file = new File([blob], `${fileContext.file.id}.json`, {
      type: "application/json",
    });

    await fileOpsService.createVersion({
      folderId,
      fileId: fileContext.file.id,
      file,
      authorEmail: userEmail,
      userName: userEmail,
      schemaUpdated: payload.resetSchema,
    });

    await fetchFileContext();
    message.success("New version created");
  };

  const runAction = async (action: () => Promise<void>): Promise<void> => {
    try {
      setSavingAction(true);
      await action();
    } catch (error) {
      const content = error instanceof Error ? error.message : "Action failed";
      message.error(content);
    } finally {
      setSavingAction(false);
    }
  };

  const handleDeployLatest = () => {
    if (!fileContext || !folderId) {
      return;
    }

    runAction(async () => {
      await fileOpsService.deployLatest({
        folderId,
        fileId: fileContext.file.id,
        authorEmail: userEmail,
        userName: userEmail,
      });

      await fetchFileContext();
      message.success("Latest version deployed");
    });
  };

  const handleCreateVersionFromPrevious = (url: string) => {
    runAction(async () => {
      const previousContent = await fileOpsService.readJsonAtUrl(url);
      setPreviousVersionContent(previousContent);
      setSafeEditorPrefillMode("previous");
      setShowSafeEditor(true);
    });
  };

  const handleUndeploy = () => {
    if (!fileContext || !folderId) {
      return;
    }

    runAction(async () => {
      await fileOpsService.undeploy({
        folderId,
        fileId: fileContext.file.id,
        authorEmail: userEmail,
        userName: userEmail,
      });

      await fetchFileContext();
      message.success("File undeployed");
    });
  };

  const handleDelete = () => {
    if (!fileContext || !folderId) {
      return;
    }

    Modal.confirm({
      title: "Delete file",
      content: "Delete is allowed only after undeploy. This removes all versions and metadata from active list.",
      okButtonProps: { danger: true },
      onOk: () =>
        runAction(async () => {
          await fileOpsService.deleteFile({
            folderId,
            fileId: fileContext.file.id,
            authorEmail: userEmail,
            userName: userEmail,
          });

          message.success("File deleted");
          onBack?.();
        }),
    });
  };

  if (!canAccess) {
    return (
      <div style={{ padding: "0 40px" }}>
        <Alert
          type="warning"
          showIcon
          message="Access denied"
          description="Your email is not allowlisted for Panchangam config management."
        />
      </div>
    );
  }

  if (!fileContext) {
    return (
      <div style={{ padding: "0 40px" }}>
        <Card>
          <Space direction="vertical">
            <Alert type="warning" showIcon message="File not found" description="The selected file was not found in registry." />
            <Button onClick={onBack}>Back to L1 File Listing</Button>
          </Space>
        </Card>
      </div>
    );
  }

  const activeFile = fileContext.file;

  return (
    <div style={{ padding: "0 40px" }}>
      <Card
        title={`L2: ${activeFile.id}`}
        extra={
          <Space>
            <Typography.Text type="secondary">{userEmail}</Typography.Text>
            <Button onClick={onBack}>Back to L1</Button>
          </Space>
        }
      >
        <FileDetailsPanel
          file={activeFile}
          latestVersion={fileContext.latestVersion}
          deployedVersion={fileContext.deployedVersion}
          isAdmin={canAdmin}
          onOpenEditor={() => {
            setSafeEditorPrefillMode("template");
            setShowSafeEditor(true);
          }}
          onOpenAdvancedEditor={() => setShowAdvancedEditor(true)}
          onCreateVersion={() => {
            setSafeEditorPrefillMode("latest");
            setShowSafeEditor(true);
          }}
          onCreateVersionFromPrevious={handleCreateVersionFromPrevious}
          onOpenDeployDiff={() => {
            Modal.confirm({
              title: `Deploy v${fileContext.latestVersion}`,
              content: `Push version ${fileContext.latestVersion} to the stable URL? This will replace the currently deployed content.`,
              okText: "Deploy",
              onOk: handleDeployLatest,
            });
          }}
          onUndeploy={handleUndeploy}
          onDelete={handleDelete}
          loadingAction={savingAction}
        />
      </Card>

      <JsonEditorModal
        mode="safe"
        visible={showSafeEditor}
        title={`Safe Editor • ${activeFile.id}`}
        initialValue={
          safeEditorPrefillMode === "latest"
            ? latestContent
            : safeEditorPrefillMode === "previous"
              ? previousVersionContent
              : getVersionTemplateContent()
        }
        canResetSchema={false}
        canEditStructure={canAdmin}
        onClose={() => setShowSafeEditor(false)}
        onSave={persistEditorSave}
      />

      <JsonEditorModal
        mode="advanced"
        visible={showAdvancedEditor}
        title={`Advanced JSON Mode • ${activeFile.id}`}
        initialValue={getVersionTemplateContent()}
        canResetSchema={canAdmin}
        canEditStructure={canAdmin}
        onClose={() => setShowAdvancedEditor(false)}
        onSave={persistEditorSave}
      />
    </div>
  );
};

export default ConfigEditorDetailScreen;
